import React, { useState, useEffect } from 'react';
import { 
  Calendar, 
  Clock, 
  Save, 
  Trash2, 
  AlertTriangle,
  Plus,
  Edit
} from 'lucide-react';
import { instanceService } from '../services/instanceService';

const ScheduleManager = () => {
  const [instances, setInstances] = useState([]);
  const [selectedInstance, setSelectedInstance] = useState('');
  const [schedule, setSchedule] = useState({
    enabled: false,
    startTime: '09:00',
    stopTime: '18:00',
    daysOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
    timezone: 'UTC'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const daysOfWeek = [
    { key: 'Monday', label: '월요일' },
    { key: 'Tuesday', label: '화요일' },
    { key: 'Wednesday', label: '수요일' },
    { key: 'Thursday', label: '목요일' },
    { key: 'Friday', label: '금요일' },
    { key: 'Saturday', label: '토요일' },
    { key: 'Sunday', label: '일요일' }
  ];

  useEffect(() => {
    loadInstances();
  }, []);

  useEffect(() => {
    if (selectedInstance) {
      loadInstanceSchedule(selectedInstance);
    }
  }, [selectedInstance]);

  const loadInstances = async () => {
    try {
      setLoading(true);
      const data = await instanceService.getInstances();
      setInstances(data);
      setError('');
    } catch (error) {
      console.error('인스턴스 로드 오류:', error);
      setError('인스턴스 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const loadInstanceSchedule = (instanceId) => {
    const instance = instances.find(i => i.instanceId === instanceId);
    if (instance?.metadata?.schedule) {
      const existingSchedule = instance.metadata.schedule;
      setSchedule({
        enabled: existingSchedule.Enabled || false,
        startTime: existingSchedule.StartTime || '09:00',
        stopTime: existingSchedule.StopTime || '18:00',
        daysOfWeek: existingSchedule.DaysOfWeek || ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        timezone: existingSchedule.Timezone || 'UTC'
      });
    } else {
      // 기본 스케줄
      setSchedule({
        enabled: false,
        startTime: '09:00',
        stopTime: '18:00',
        daysOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        timezone: 'UTC'
      });
    }
  };

  const handleScheduleChange = (key, value) => {
    setSchedule(prev => ({
      ...prev,
      [key]: value
    }));
  };

  const handleDayToggle = (day) => {
    setSchedule(prev => ({
      ...prev,
      daysOfWeek: prev.daysOfWeek.includes(day)
        ? prev.daysOfWeek.filter(d => d !== day)
        : [...prev.daysOfWeek, day]
    }));
  };

  const validateSchedule = () => {
    if (!selectedInstance) {
      setError('인스턴스를 선택해주세요.');
      return false;
    }

    if (schedule.enabled) {
      if (!schedule.startTime || !schedule.stopTime) {
        setError('시작 시간과 종료 시간을 입력해주세요.');
        return false;
      }

      if (schedule.startTime >= schedule.stopTime) {
        setError('시작 시간은 종료 시간보다 이전이어야 합니다.');
        return false;
      }

      if (schedule.daysOfWeek.length === 0) {
        setError('최소 하나의 요일을 선택해주세요.');
        return false;
      }
    }

    return true;
  };

  const saveSchedule = async () => {
    if (!validateSchedule()) {
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const scheduleData = {
        Enabled: schedule.enabled,
        StartTime: schedule.startTime,
        StopTime: schedule.stopTime,
        DaysOfWeek: schedule.daysOfWeek,
        Timezone: schedule.timezone
      };

      await instanceService.setSchedule(selectedInstance, scheduleData);
      setSuccess('스케줄이 성공적으로 저장되었습니다.');
      
      // 인스턴스 목록 새로고침
      await loadInstances();
    } catch (error) {
      console.error('스케줄 저장 오류:', error);
      setError('스케줄 저장에 실패했습니다: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const removeSchedule = async () => {
    if (!selectedInstance) return;

    if (!window.confirm('이 인스턴스의 스케줄을 제거하시겠습니까?')) {
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const scheduleData = {
        Enabled: false,
        StartTime: '',
        StopTime: '',
        DaysOfWeek: [],
        Timezone: 'UTC'
      };

      await instanceService.setSchedule(selectedInstance, scheduleData);
      setSuccess('스케줄이 제거되었습니다.');
      
      // 스케줄 폼 초기화
      setSchedule({
        enabled: false,
        startTime: '09:00',
        stopTime: '18:00',
        daysOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
        timezone: 'UTC'
      });
      
      // 인스턴스 목록 새로고침
      await loadInstances();
    } catch (error) {
      console.error('스케줄 제거 오류:', error);
      setError('스케줄 제거에 실패했습니다: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">스케줄 관리자 로딩 중...</p>
        </div>
      </div>
    );
  }

  const selectedInstanceData = instances.find(i => i.instanceId === selectedInstance);

  return (
    <div className="space-y-6">
      {/* 헤더 */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">스케줄 관리</h1>
        <p className="mt-1 text-sm text-gray-600">
          EC2 인스턴스의 자동 시작/중지 스케줄을 설정하고 관리합니다
        </p>
      </div>

      {/* 에러/성공 메시지 */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            <div className="ml-3">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          </div>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-md p-4">
          <div className="flex">
            <Calendar className="h-5 w-5 text-green-400" />
            <div className="ml-3">
              <p className="text-sm text-green-700">{success}</p>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 인스턴스 선택 */}
        <div className="bg-white shadow rounded-lg p-6">
          <h2 className="text-lg font-medium text-gray-900 mb-4">인스턴스 선택</h2>
          
          <div className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                인스턴스
              </label>
              <select
                value={selectedInstance}
                onChange={(e) => setSelectedInstance(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">인스턴스를 선택하세요</option>
                {instances.map(instance => (
                  <option key={instance.instanceId} value={instance.instanceId}>
                    {instance.tags?.Name || instance.instanceId} ({instance.state})
                  </option>
                ))}
              </select>
            </div>

            {selectedInstanceData && (
              <div className="mt-4 p-3 bg-gray-50 rounded-md">
                <h3 className="text-sm font-medium text-gray-900 mb-2">선택된 인스턴스</h3>
                <div className="space-y-1 text-sm text-gray-600">
                  <p><span className="font-medium">ID:</span> {selectedInstanceData.instanceId}</p>
                  <p><span className="font-medium">타입:</span> {selectedInstanceData.instanceType}</p>
                  <p><span className="font-medium">상태:</span> {selectedInstanceData.state}</p>
                  <p><span className="font-medium">가용 영역:</span> {selectedInstanceData.availabilityZone}</p>
                  {selectedInstanceData.metadata?.schedule?.Enabled && (
                    <p className="text-green-600 font-medium">✓ 스케줄 활성화됨</p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* 스케줄 설정 */}
        <div className="bg-white shadow rounded-lg p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-gray-900">스케줄 설정</h2>
            {selectedInstance && (
              <button
                onClick={removeSchedule}
                disabled={saving}
                className="text-red-600 hover:text-red-700 flex items-center space-x-1 text-sm"
              >
                <Trash2 size={16} />
                <span>스케줄 제거</span>
              </button>
            )}
          </div>

          <div className="space-y-4">
            {/* 스케줄 활성화 */}
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="enabled"
                checked={schedule.enabled}
                onChange={(e) => handleScheduleChange('enabled', e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <label htmlFor="enabled" className="text-sm font-medium text-gray-700">
                자동 스케줄링 활성화
              </label>
            </div>

            {schedule.enabled && (
              <>
                {/* 시간 설정 */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      시작 시간
                    </label>
                    <input
                      type="time"
                      value={schedule.startTime}
                      onChange={(e) => handleScheduleChange('startTime', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      종료 시간
                    </label>
                    <input
                      type="time"
                      value={schedule.stopTime}
                      onChange={(e) => handleScheduleChange('stopTime', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>

                {/* 요일 선택 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    실행 요일
                  </label>
                  <div className="grid grid-cols-7 gap-2">
                    {daysOfWeek.map(day => (
                      <button
                        key={day.key}
                        type="button"
                        onClick={() => handleDayToggle(day.key)}
                        className={`p-2 text-xs rounded-md transition-colors ${
                          schedule.daysOfWeek.includes(day.key)
                            ? 'bg-blue-600 text-white'
                            : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                        }`}
                      >
                        {day.label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* 타임존 */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    타임존
                  </label>
                  <select
                    value={schedule.timezone}
                    onChange={(e) => handleScheduleChange('timezone', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="UTC">UTC</option>
                    <option value="Asia/Seoul">Asia/Seoul (KST)</option>
                    <option value="America/New_York">America/New_York (EST)</option>
                    <option value="Europe/London">Europe/London (GMT)</option>
                  </select>
                </div>
              </>
            )}

            {/* 저장 버튼 */}
            <div className="pt-4">
              <button
                onClick={saveSchedule}
                disabled={!selectedInstance || saving}
                className="btn-primary w-full flex items-center justify-center space-x-2"
              >
                {saving ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <Save size={16} />
                )}
                <span>{saving ? '저장 중...' : '스케줄 저장'}</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* 스케줄된 인스턴스 목록 */}
      <div className="bg-white shadow rounded-lg p-6">
        <h2 className="text-lg font-medium text-gray-900 mb-4">스케줄된 인스턴스</h2>
        
        {instances.filter(i => i.metadata?.schedule?.Enabled).length === 0 ? (
          <p className="text-gray-500 text-center py-4">
            스케줄이 설정된 인스턴스가 없습니다.
          </p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {instances
              .filter(i => i.metadata?.schedule?.Enabled)
              .map(instance => (
                <div key={instance.instanceId} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-medium text-gray-900">
                      {instance.tags?.Name || instance.instanceId}
                    </h3>
                    <button
                      onClick={() => setSelectedInstance(instance.instanceId)}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      <Edit size={16} />
                    </button>
                  </div>
                  <div className="space-y-1 text-sm text-gray-600">
                    <div className="flex items-center space-x-1">
                      <Clock size={14} />
                      <span>
                        {instance.metadata.schedule.StartTime} - {instance.metadata.schedule.StopTime}
                      </span>
                    </div>
                    <p className="text-xs">
                      {instance.metadata.schedule.DaysOfWeek?.join(', ')}
                    </p>
                  </div>
                </div>
              ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ScheduleManager;